import type {
  Attribute,
  BrandBase,
  Breadcrumb,
  Category,
  GalleryItem,
  Image,
  Label,
  Price,
  ProductVariant,
  Seo,
  Stock,
  VariantAttribute,
} from '@maestro/data-layer'

export interface ProductByIdOptionsType {
  isEnableClientSidePriceFetching?: boolean
  isEnableClientSideStockFetching?: boolean
}

export interface ProductDetailsByIdOptionsType extends ProductByIdOptionsType {
  fields?: string[]
}

export interface ManualRecommendationsByRelationOptionsType extends ProductByIdOptionsType {
  relationName: string
  product: Product
}

export interface ProductBase {
  /**
   * The unique identifier for the product
   */
  id: string
  /**
   * Product path including main category (if applicable), without leading slash
   */
  urlPath: string
  name: string
  shortDescription?: string
  seo?: Seo
  brand?: BrandBase
  breadcrumbs?: Breadcrumb[]
  categories?: Category[]
  image?: Image
  price: Price
  gallery: GalleryItem[]
  /**
   * Featured attributes, key decision drivers - "400W", "2 slices", "Eco friendly"
   */
  highlightedAttributes?: Attribute[]
  stock?: Stock
  /**
   * Variant attributes
   */
  variantAttributes: VariantAttribute[]
  defaultVariantId: string
  variants: ProductVariant[]
  productType: ProductType
  relatedProducts?: RelatedProductRelation[]
  isBundle?: boolean
  bundleItems?: BundleItem[]
  isNonSaleable?: boolean
}

export interface RelatedProductRelation {
  key: 'siblings' | 'othersAlsoBought' | string
  productIds: string[]
}

export interface BundleItem {
  quantity: number
  isRequired: boolean
  skuId?: string
  product: Product
}

export interface Product extends ProductBase {
  sku?: string
  skus?: string[]
  description?: string
  mainCategory?: Category
  /**
   * Full specification under more product info/product details - width, height etc.
   */
  attributes?: Attribute[]
  labels?: Label[]
  type?: string
  documents?: ProductDocument[]
  eligibleForSubscription?: boolean
  packagingUnit?: string // Sales/packaging unit, e.g. "Eske (6 × Stk)"
}

export interface ProductDetails
  extends
    Partial<Omit<Product, 'id' | 'attributes' | 'description'>>,
    Required<Pick<Product, 'id' | 'attributes' | 'description'>> {}

export interface ProductDocument {
  url: string
  title: string
  fileType?: string
  fileName?: string
  size?: number
  isExternalLink?: boolean
}

export enum ProductType {
  Simple = 'simple',
  Variant = 'variant',
}
